/**
 * Rate limiter for ERDDAP requests to prevent server overload
 */

interface QueueItem {
  execute: () => Promise<any>;
  resolve: (value: any) => void;
  reject: (error: any) => void;
}

export class RateLimiter {
  private queue: QueueItem[] = [];
  private isProcessing = false;
  private lastRequestTime = 0;
  private readonly minDelayBetweenRequests: number;
  private readonly maxConcurrentRequests: number;
  private activeRequests = 0;

  constructor(
    minDelayBetweenRequests: number = 1000, // 1 second between requests
    maxConcurrentRequests: number = 2 // Max 2 concurrent requests
  ) {
    this.minDelayBetweenRequests = minDelayBetweenRequests;
    this.maxConcurrentRequests = maxConcurrentRequests;
  }

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push({
        execute: fn,
        resolve,
        reject
      });
      this.processQueue();
    });
  }

  private async processQueue() {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    // Check if we've hit the concurrent request limit
    if (this.activeRequests >= this.maxConcurrentRequests) {
      return;
    }

    this.isProcessing = true;

    // Calculate time to wait before next request
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    const timeToWait = Math.max(0, this.minDelayBetweenRequests - timeSinceLastRequest);

    // Wait if necessary
    if (timeToWait > 0) {
      await new Promise(resolve => setTimeout(resolve, timeToWait));
    }

    // Process items from queue up to concurrent limit
    const itemsToProcess: QueueItem[] = [];
    while (this.queue.length > 0 && this.activeRequests < this.maxConcurrentRequests) {
      const item = this.queue.shift();
      if (item) {
        itemsToProcess.push(item);
        this.activeRequests++;
      }
    }

    this.lastRequestTime = Date.now();

    // Execute all items concurrently
    await Promise.all(
      itemsToProcess.map(async (item) => {
        try {
          const result = await item.execute();
          item.resolve(result);
        } catch (error) {
          item.reject(error);
        } finally {
          this.activeRequests--;
          // Continue processing queue after each request completes
          setTimeout(() => this.processQueue(), 0);
        }
      })
    );

    this.isProcessing = false;

    // Continue processing if there are more items
    if (this.queue.length > 0) {
      setTimeout(() => this.processQueue(), 0);
    }
  }

  getQueueLength(): number {
    return this.queue.length;
  }

  getActiveRequests(): number {
    return this.activeRequests;
  }
}

import { getConfigValue } from '../config/erddapConfig';

// Create singleton instance for ERDDAP requests
export const erddapRateLimiter = new RateLimiter(
  getConfigValue('MIN_REQUEST_INTERVAL_MS'),
  getConfigValue('MAX_CONCURRENT_REQUESTS')
);